import { createSelector } from "@reduxjs/toolkit";

import { CreateOrganizationNamespace } from "./constants";

import type { Organization, OrganizationType } from "@packages/client";
import type { RootState } from "Store/configureStore";
import {
  selectFeatureProjectSetupTipsEnabled,
  selectFeatureProjectSetupWizardEnabled
} from "Reducers/featureFlags/featureFlags.selectors";

const FIXED_ORGANIZATION_TYPE: OrganizationType = "fixed";

/**
 * Selects the current organization based on the organizationId from route params.
 */
export const selectCurrentOrganization = createSelector(
  (state: RootState) => state.app.routeHistory?.params.organizationId,
  (state: RootState) => state.organization.data,
  (currentOrganizationNameOrId, organizations) => {
    if (!organizations || !currentOrganizationNameOrId) return undefined;
    for (const organization of Object.values(organizations))
      if (organization?.name === currentOrganizationNameOrId)
        return organization;
  }
);

/**
 * Returns true for flex organizations, false for fixed organizations.
 * Used to determine project creation flow: flex uses new flow, fixed uses old wizard.
 *
 * IMPORTANT: Defaults to true when organization isn't loaded yet.
 * This is intentional because all new organizations are flex, and we want
 * new users to see the new flow immediately without waiting for org data.
 * Only explicitly fixed organizations should use the old wizard.
 */
export const selectIsFlexOrganization = createSelector(
  selectCurrentOrganization,
  organization => organization?.type !== FIXED_ORGANIZATION_TYPE
);

export const organizationStateSelector = (state: RootState) =>
  state.organization;

export const organizationLoadingSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.loading
);

// Whether a loadOrganizations call has settled (fulfilled or rejected) at
// least once, to tell "no organizations" apart from "not loaded yet" (CON-1662).
export const organizationsLoadedSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.loaded
);

export const orgByDescriptionFieldSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.orgByDescriptionField
);

export const getOrganizations = createSelector(
  orgByDescriptionFieldSelector,
  orgByDescriptionField => Object.values(orgByDescriptionField)
);

export const organizationByNameOrIdSelector = createSelector(
  orgByDescriptionFieldSelector,
  (_: RootState, params: { organizationNameOrId: string }) =>
    params.organizationNameOrId,
  (organizations, nameOrId) =>
    Object.values(organizations).find(
      organization =>
        organization?.name === nameOrId || organization?.id === nameOrId
    )
);

export const organizationByDescriptionIdSelector = createSelector(
  orgByDescriptionFieldSelector,
  (_: RootState, params: { organizationDescriptionId?: string }) => params,
  (orgByDescriptionField, { organizationDescriptionId }) =>
    organizationDescriptionId
      ? orgByDescriptionField?.[organizationDescriptionId]
      : undefined
);

export const organizationsSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.data
);

export const organizationSelector = createSelector(
  organizationsSelector,
  (_: RootState, params: { organizationId?: string }) => params,
  (organizationData, { organizationId }) =>
    organizationId ? organizationData?.[organizationId] : undefined
);

export const organizationListSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.list
);

export const organizationErrorsSelector = createSelector(
  organizationStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (organizationState, { organizationId }) =>
    organizationState?.errors?.[organizationId]
);

export const organizationInflightSelector = createSelector(
  organizationStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (organizationState, { organizationId }) =>
    organizationState?.inflight?.[organizationId]
);

export const organization_ErrorsSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?._errors
);

export const organization_ErrorSelector = createSelector(
  organization_ErrorsSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (_errors, { organizationId }) => _errors?.[organizationId]
);

export const createdOrganizationSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.[CreateOrganizationNamespace]
);

export const organizationLoadingErrorSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.organizationLoadingError
);

// Unlike organizationLoadingErrorSelector (shared with getOrganization, which
// can be rejecting for an unrelated single org), this reflects only
// loadOrganizations' own last outcome, and clears itself on its next
// pending/fulfilled (CON-1662).
export const loadOrganizationsErrorSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.loadOrganizationsError
);

// An empty `organizations` map is only proof of "no organization" once
// loadOrganizations has actually settled successfully: while it's still
// loading (or hasn't been dispatched yet) or it failed, a real member must
// not be told they have no organization just because the load hasn't
// finished or errored out (CON-1662).
export const hasNoOrganizationSelector = createSelector(
  organizationsSelector,
  organizationsLoadedSelector,
  loadOrganizationsErrorSelector,
  (organizations, organizationsLoaded, loadOrganizationsError) =>
    Boolean(
      organizationsLoaded &&
      !loadOrganizationsError &&
      (!organizations || Object.keys(organizations).length === 0)
    )
);

export const selectDeletedOrganizations = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.deleted
);

export const deletedOrganizationSelector = createSelector(
  selectDeletedOrganizations,
  (_: RootState, params: { organizationId: string }) => params,
  (deletedOrganizations, { organizationId }) =>
    deletedOrganizations?.[organizationId]
);

export const isLoadingDeleteOrganizationSelector = createSelector(
  selectDeletedOrganizations,
  deletedOrganizations => deletedOrganizations?.loading
);

export const selectOrgEstimates = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.organizationEstimate
);

export const organizationEstimateSelector = createSelector(
  selectOrgEstimates,
  (_: RootState, params: { organizationId: string }) => params.organizationId,
  (organizationEstimate, organizationId) =>
    organizationEstimate?.[organizationId]
);

export const organizationEstimateSubscriptionSelector = createSelector(
  organizationEstimateSelector,
  (_: RootState, params: { subscriptionId: string }) => params,
  (estimate, { subscriptionId }) =>
    estimate?.current?.subscriptions?.list?.find(
      subscription => `${subscription.license_id}` === subscriptionId
    )
);

export const organizationsMemberOrOwnerOfSelector = createSelector(
  organizationListSelector,
  getOrganizations,
  (organizationList, organizations) =>
    organizations.filter(
      (organization): organization is Organization =>
        !!organizationList.find(id => organization?.id === id)
    )
);

export const canCreateProjectOrganizationsSelector = createSelector(
  getOrganizations,
  organizations =>
    organizations.filter(
      (organization): organization is Organization =>
        !!organization?.hasLink("create-subscription")
    )
);

export const hasFixedOrganizationsSelector = createSelector(
  getOrganizations,
  organizations =>
    organizations.some(
      (organization): organization is Organization =>
        organization?.type === FIXED_ORGANIZATION_TYPE
    )
);

export const hasFixedAndActiveOrganizationsSelector = createSelector(
  getOrganizations,
  organizations =>
    organizations.some(
      (organization): organization is Organization =>
        organization?.type === FIXED_ORGANIZATION_TYPE &&
        organization?.status === "active"
    )
);

/**
 * Type representing which wizard/content to show on a project page.
 * - 'loading': Organization not yet loaded, show nothing
 * - 'connect-integrations': Flex org, show ConnectIntegrations form
 * - 'setup-tips': Fixed org with setup tips enabled
 * - 'wizard': Fixed org with legacy wizard enabled
 * - 'none': No wizard should be shown
 */
export type WizardType =
  "loading" | "connect-integrations" | "setup-tips" | "wizard" | "none";

/**
 * Determines which wizard/content component to render based on:
 * - Organization loading state
 * - Organization type (flex vs fixed)
 * - Feature flags for setup tips and wizard
 *
 * This keeps the decision logic in Redux (selector) while
 * React components remain "dumb views".
 */
export const selectWizardType = createSelector(
  selectCurrentOrganization,
  organizationLoadingSelector,
  selectIsFlexOrganization,
  selectFeatureProjectSetupTipsEnabled,
  selectFeatureProjectSetupWizardEnabled,
  (
    organization,
    isLoading,
    isFlexOrganization,
    setupTipsEnabled,
    setupWizardEnabled
  ): WizardType => {
    // Don't render anything while loading or if organization not found
    if (isLoading || !organization) return "loading";

    // Flex orgs always use the new ConnectIntegrations flow
    if (isFlexOrganization) return "connect-integrations";

    // Fixed orgs: check feature flags in priority order
    if (setupTipsEnabled) return "setup-tips";
    if (setupWizardEnabled) return "wizard";

    return "none";
  }
);

/**
 * Checks if the current organization (from route) is of type "flexible".
 * Returns false if no organization is found in state.
 *
 * NOTE: This differs from `selectIsFlexOrganization` in how it handles undefined:
 * - `selectIsFlexOrganization`: returns TRUE when org is undefined (optimistic for new users)
 * - `selectCurrentOrganizationIsFlexible`: returns FALSE when org is undefined (conservative)
 *
 * Use this selector when you need to gate features that should NOT appear until
 * we've confirmed the org is actually flexible.
 */
export const selectCurrentOrganizationIsFlexible = createSelector(
  selectCurrentOrganization,
  organization => organization?.type === "flexible"
);

/**
 * Returns true when the projects page is showing the flex first-project setup,
 * which already offers project creation. The navbar reads it to drop its own
 * "Create project" button for as long as that page is on screen.
 *
 * The two halves come from the same places the page uses to reach that screen:
 * the optimistic flex predicate `CreateFirstProject` branches on, and the
 * organization the page itself published when it rendered the setup. Nothing
 * here re-derives whether the organization has projects — the page holds that
 * answer in component state, and the `organizationProjects` slice only ever
 * carries the provisioning/failed projects the puller fetches.
 */
export const selectIsFlexOrgWithNoProjects = createSelector(
  selectIsFlexOrganization,
  selectCurrentOrganization,
  (state: RootState) =>
    state.organizationProjects?.firstProjectSetupOrganizationId,
  (isFlexOrganization, organization, firstProjectSetupOrganizationId) =>
    isFlexOrganization &&
    !!organization &&
    firstProjectSetupOrganizationId === organization.id
);
